import React, { useState, useEffect, useRef } from 'react';
import { db, BACKEND_URL } from '../../firebase/config';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { 
  ArrowLeft, Download, Upload, Camera, Trash2, Edit, X,
  User, Phone, Mail, MapPin, Calendar, Heart, Award, Users,
  GraduationCap, BookOpen, ExternalLink
} from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { createStudentViewPath, useHiddenStudentRoute } from '../../utils/studentRoutes';
import { getStudentPhotoUrl } from '../../utils/mediaUrls';

function StudentMahiti() {
  const { rollNumber } = useHiddenStudentRoute('/student/mahiti');
  const profileRef = useRef();
  const fileInputRef = useRef();
  
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  useEffect(() => {
    if (!rollNumber) {
      setLoading(false);
      return;
    }
    loadStudentData();
  }, [rollNumber]);

  const fetchMentorAndCoordinator = async (batch, division) => {
    try {
      let mentorName = 'Not Assigned';
      let ccName = 'Not Assigned';

      if (batch) {
        const batchQuery = query(
          collection(db, 'batchAssignments'),
          where('batch', '==', batch)
        );
        const batchSnapshot = await getDocs(batchQuery);
        
        if (!batchSnapshot.empty) {
          const batchData = batchSnapshot.docs[0].data();
          mentorName = batchData.mentorName || 'Not Assigned';
        }
      }

      if (division) {
        const divisionQuery = query(
          collection(db, 'divisionAssignments'),
          where('division', '==', division)
        );
        const divisionSnapshot = await getDocs(divisionQuery);
        
        if (!divisionSnapshot.empty) {
          const divisionData = divisionSnapshot.docs[0].data();
          ccName = divisionData.coordinatorName || 'Not Assigned';
        }
      }

      return { mentorName, ccName };
    } catch (error) {
      console.error('Error fetching mentor/coordinator:', error);
      return { mentorName: 'Error loading', ccName: 'Error loading' };
    }
  };

  const loadStudentData = async () => {
    try {
      const studentDoc = await getDoc(doc(db, 'students', rollNumber));
      if (studentDoc.exists()) {
        const studentData = studentDoc.data();
        
        const { mentorName, ccName } = await fetchMentorAndCoordinator(
          studentData.batch,
          studentData.division
        );
        
        setStudent({
          ...studentData,
          mentorName: mentorName,
          ccName: ccName
        });
      }
      setLoading(false);
    } catch (error) {
      console.error('Error loading student data:', error);
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('rollNumber', rollNumber);
      formData.append('category', 'photos');
      formData.append('file', file);
      
      if (student.photoPath) {
        formData.append('oldPhotoPath', student.photoPath);
      } else if (student.photoUrl) {
        formData.append('oldPhotoUrl', student.photoUrl);
      }

      const response = await fetch(`${BACKEND_URL}/api/upload/photo`, {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (result.success && result.photoUrl) {
        const studentRef = doc(db, 'students', rollNumber);
        await updateDoc(studentRef, {
          photoUrl: result.photoUrl,
          photoPath: result.photoPath || null,
          photoUpdatedAt: new Date()
        });

        setStudent(prev => ({
          ...prev,
          photoUrl: result.photoUrl,
          photoPath: result.photoPath || null
        }));

        alert('✓ Photo uploaded successfully!');
        setShowUploadModal(false);
      } else {
        throw new Error(result.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error: ' + error.message);
    }
    
    setUploading(false);
  };

  const handleDeletePhoto = async () => {
    if (!window.confirm('Are you sure you want to delete this photo?')) {
      return;
    }

    try {
      const deleteResponse = await fetch(`${BACKEND_URL}/api/upload/photo/${rollNumber}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          photoPath: student.photoPath,
          photoUrl: student.photoUrl
        })
      });

      const deleteResult = await deleteResponse.json();

      if (!deleteResult.success) {
        throw new Error(deleteResult.error || 'Failed to delete photo file');
      }

      const studentRef = doc(db, 'students', rollNumber);
      await updateDoc(studentRef, {
        photoUrl: null,
        photoPath: null,
        photoDeletedAt: new Date()
      });

      setStudent(prev => ({
        ...prev,
        photoUrl: null,
        photoPath: null
      }));

      alert('✓ Photo deleted successfully!');
    } catch (error) {
      console.error('Error deleting photo:', error);
      alert('Error: ' + error.message);
    }
  };

  const handleSavePDF = async () => {
    const element = profileRef.current;
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff'
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    
    const pdfWidth = 210;
    const pdfHeight = 297;
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
    
    pdf.addImage(imgData, 'PNG', 0, 0, imgWidth * ratio, imgHeight * ratio);
    pdf.save(`${rollNumber}_mahiti.pdf`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading student information...</p>
        </div>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <User className="w-20 h-20 text-gray-400 mx-auto mb-4" />
          <p className="text-xl text-gray-600 font-semibold">Student not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-8 print:bg-white print:py-0">
      <div className="max-w-6xl mx-auto px-4 print:px-0">
        {/* Action Buttons */}
        <div className="flex justify-between items-center mb-6 print:hidden">
          <button 
            onClick={() => window.close()} 
            className="flex items-center space-x-2 px-6 py-3 bg-white text-gray-700 rounded-xl hover:bg-gray-50 shadow-md hover:shadow-lg transition-all duration-200"
          >
            <X className="w-5 h-5" />
            <span className="font-medium">Close</span>
          </button>
          <div className="flex space-x-3">
            <button 
              onClick={handleSavePDF} 
              className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 shadow-md hover:shadow-lg transition-all duration-200"
            >
              <Download className="w-5 h-5" />
              <span className="font-medium">Save PDF</span>
            </button>
          </div>
        </div>

        {/* Main Profile Card */}
        <div ref={profileRef} className="bg-white rounded-2xl shadow-xl overflow-hidden print:shadow-none print:rounded-none">
          {/* Header Section with Gradient */}
          <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white mb-1">Student Information</h1>
                <p className="text-indigo-100">Academic Year 2025-26 | Semester 1</p>
              </div>
              <div className="bg-white bg-opacity-20 backdrop-blur-sm px-6 py-3 rounded-xl">
                <p className="text-white text-sm font-medium">Roll Number</p>
                <p className="text-white text-2xl font-bold">{student.rollNumber}</p>
              </div>
            </div>
          </div>

          {/* Content Grid */}
          <div className="p-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left Column - Photo and Quick Info */}
              <div className="lg:col-span-1 space-y-6">
                {/* Photo Card */}
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 shadow-inner">
                  <div 
                    className="relative bg-white rounded-xl overflow-hidden shadow-lg group"
                    style={{ aspectRatio: '3/4' }}
                  >
                    {getStudentPhotoUrl(student) ? (
                      <>
                        <img 
                          src={getStudentPhotoUrl(student)} 
                          alt="Student" 
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = '';
                            e.target.style.display = 'none';
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-end justify-center gap-3 pb-6 print:hidden">
                          <button
                            onClick={() => setShowUploadModal(true)}
                            className="p-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-all shadow-lg hover:scale-110"
                            title="Replace Photo"
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                          <button
                            onClick={handleDeletePhoto}
                            className="p-3 bg-red-600 text-white rounded-full hover:bg-red-700 transition-all shadow-lg hover:scale-110"
                            title="Delete Photo"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex flex-col items-center justify-center h-full bg-gradient-to-br from-gray-100 to-gray-200">
                          <Camera className="w-16 h-16 text-gray-400 mb-3" />
                          <p className="text-sm text-gray-500 font-medium">No Photo</p>
                        </div>
                        <button
                          onClick={() => setShowUploadModal(true)}
                          className="absolute bottom-4 right-4 p-3 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 shadow-lg hover:scale-110 transition-all print:hidden"
                          title="Upload Photo"
                        >
                          <Upload className="w-5 h-5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Quick Info Card */}
                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 space-y-4">
                  <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                    <Award className="w-5 h-5 text-indigo-600" />
                    Quick Info
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <GraduationCap className="w-5 h-5 text-indigo-600 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Branch</p>
                        <p className="text-sm font-semibold text-gray-800">{student.branch || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <BookOpen className="w-5 h-5 text-purple-600 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Division / Batch</p>
                        <p className="text-sm font-semibold text-gray-800">{student.division} / {student.batch || 'N/A'}</p>
                      </div>
                    </div>
                  <div className="flex items-start gap-3">
                      <Heart className="w-5 h-5 text-red-500 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Blood Group</p>
                        <p className="text-sm font-semibold text-gray-800">{student.bloodGroup || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Award className="w-5 h-5 text-orange-600 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-500 font-medium">ABC ID</p>
                        <p className="text-sm font-semibold text-gray-800">{student.abcId || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <User className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Mentor</p>
                        <p className="text-sm font-semibold text-gray-800">{student.mentorName || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Users className="w-5 h-5 text-green-600 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Class Coordinator</p>
                        <p className="text-sm font-semibold text-gray-800">{student.ccName || 'N/A'}</p>
                      </div>
                    </div>
                    
                  </div>
                </div>

                {/* Reports Links */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 space-y-3 print:hidden">
                  <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-4">
                    <BookOpen className="w-5 h-5 text-blue-600" />
                    Reports
                  </h3>
                  <button
                    onClick={() => window.open(createStudentViewPath('/attendance-report', rollNumber, '?popup=true'), 'AttendanceReport', 'width=1200,height=800,scrollbars=yes')}
                    className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-blue-50 rounded-xl border-2 border-blue-200 hover:border-blue-400 transition-all duration-200 group"
                  >
                    <span className="text-sm font-semibold text-gray-800">View Attendance Report</span>
                    <ExternalLink className="w-4 h-4 text-blue-600 group-hover:translate-x-1 transition-transform" />
                  </button>
                  <button
                    onClick={() => window.open(createStudentViewPath('/iat1-report', rollNumber, '?popup=true'), 'IAT1Report', 'width=1200,height=800,scrollbars=yes')}
                    className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-purple-50 rounded-xl border-2 border-purple-200 hover:border-purple-400 transition-all duration-200 group"
                  >
                    <span className="text-sm font-semibold text-gray-800">IAT-1 Report</span>
                    <ExternalLink className="w-4 h-4 text-purple-600 group-hover:translate-x-1 transition-transform" />
                  </button>
                  <button
                    onClick={() => window.open(createStudentViewPath('/iat2-report', rollNumber, '?popup=true'), 'IAT2Report', 'width=1200,height=800,scrollbars=yes')}
                    className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-indigo-50 rounded-xl border-2 border-indigo-200 hover:border-indigo-400 transition-all duration-200 group"
                  >
                    <span className="text-sm font-semibold text-gray-800">IAT-2 Report</span>
                    <ExternalLink className="w-4 h-4 text-indigo-600 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>

              {/* Right Column - Detailed Information */}
              <div className="lg:col-span-2 space-y-6">
                {/* Personal Information Section */}
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-lg">
                      <User className="w-5 h-5 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800">Personal Information</h2>
                  </div>
                  
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 space-y-4">
                    {/* Name */}
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Full Name</label>
                      <p className="text-lg font-bold text-gray-900 mt-1">
                        {student.firstName} {student.fatherName} {student.surname}
                      </p>
                    </div>

                    {/* Row: Gender, DOB */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-white rounded-lg p-4 shadow-sm">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Gender</label>
                        <p className="text-base font-semibold text-gray-900 mt-1">{student.gender || 'N/A'}</p>
                      </div>
                      <div className="bg-white rounded-lg p-4 shadow-sm">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Date of Birth
                        </label>
                        <p className="text-base font-semibold text-gray-900 mt-1">{student.dob || 'N/A'}</p>
                      </div>
                    </div>

                    {/* Aadhar */}
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Aadhar Card Number</label>
                      <p className="text-base font-semibold text-gray-900 mt-1">{student.aadharCard || 'N/A'}</p>
                    </div>

                    {/* Permanent Address */}
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        Permanent Address
                      </label>
                      <p className="text-base font-semibold text-gray-900 mt-1">{student.permanentAddress || student.address || 'N/A'}</p>
                    </div>

                    {/* Current Address */}
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        Current Address
                      </label>
                      <p className="text-base font-semibold text-gray-900 mt-1">{student.currentAddress || student.address || 'N/A'}</p>
                    </div>

                    {/* Hobbies */}
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Personal Hobbies / Interests</label>
                      <p className="text-base font-semibold text-gray-900 mt-1">{student.hobbies || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Contact Information Section */}
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-lg">
                      <Phone className="w-5 h-5 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800">Contact Information</h2>
                  </div>
                  
                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-white rounded-lg p-4 shadow-sm">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          Mobile Number
                        </label>
                        <p className="text-base font-semibold text-gray-900 mt-1">{student.mobile || 'N/A'}</p>
                      </div>
                      <div className="bg-white rounded-lg p-4 shadow-sm">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">WhatsApp Number</label>
                        <p className="text-base font-semibold text-gray-900 mt-1">{student.whatsapp || 'N/A'}</p>
                      </div>
                      <div className="bg-white rounded-lg p-4 shadow-sm md:col-span-2">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          Email ID
                        </label>
                        <p className="text-base font-semibold text-gray-900 mt-1">{student.email || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Parent's Information Section */}
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-gradient-to-br from-green-600 to-emerald-600 rounded-lg">
                      <Users className="w-5 h-5 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800">Parent's Details</h2>
                  </div>
                  
                  <div className="space-y-4">
                    {/* Father's Details */}
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6">
                      <h3 className="text-lg font-bold text-gray-800 mb-4">Father's Details</h3>
                      <div className="space-y-4">
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Father's Name</label>
                          <p className="text-base font-semibold text-gray-900 mt-1">{student.fatherName || 'N/A'}</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="bg-white rounded-lg p-4 shadow-sm">
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Mobile (WhatsApp)</label>
                            <p className="text-base font-semibold text-gray-900 mt-1">{student.fatherMobile || 'N/A'}</p>
                          </div>
                          <div className="bg-white rounded-lg p-4 shadow-sm">
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Occupation</label>
                            <p className="text-base font-semibold text-gray-900 mt-1">{student.fatherOccupation || 'N/A'}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Mother's Details */}
                    <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-xl p-6">
                      <h3 className="text-lg font-bold text-gray-800 mb-4">Mother's Details</h3>
                      <div className="space-y-4">
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Mother's Name</label>
                          <p className="text-base font-semibold text-gray-900 mt-1">{student.motherName || 'N/A'}</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="bg-white rounded-lg p-4 shadow-sm">
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Mobile (WhatsApp)</label>
                            <p className="text-base font-semibold text-gray-900 mt-1">{student.motherMobile || 'N/A'}</p>
                          </div>
                          <div className="bg-white rounded-lg p-4 shadow-sm">
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Occupation</label>
                            <p className="text-base font-semibold text-gray-900 mt-1">{student.motherOccupation || 'N/A'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-8 pt-6 border-t-2 border-gray-200">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  <p className="font-semibold">Yashwantrao Bhonsale Institute of Technology</p>
                  <p className="text-xs mt-1">Bhonsale Knowledge City, Vazarwadi, Charathe, Sawantwadi, Sindhudurg 416510</p>
                  <p className="text-xs">DTE CODE: 3470 | MSBTE CODE: 1742</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-700 mb-8">Student's Signature</p>
                  <div className="border-t-2 border-gray-400 w-40"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 print:hidden animate-fadeIn">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl animate-slideUp">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Camera className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Upload Student Photo</h3>
              <p className="text-sm text-gray-600">Select a clear photo (JPG/PNG, max 5MB)</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoUpload}
              disabled={uploading}
              style={{ display: 'none' }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-full py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed mb-3 font-semibold shadow-lg hover:shadow-xl transition-all"
            >
              {uploading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Uploading...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Upload className="w-5 h-5" />
                  Choose Photo
                </span>
              )}
            </button>
            <button
              onClick={() => setShowUploadModal(false)}
              disabled={uploading}
              className="w-full py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-all"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(20px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
        
        @media print {
          body { margin: 0; padding: 0; }
          .print\\:hidden { display: none !important; }
          .print\\:bg-white { background: white !important; }
          .print\\:py-0 { padding-top: 0 !important; padding-bottom: 0 !important; }
          .print\\:px-0 { padding-left: 0 !important; padding-right: 0 !important; }
          .print\\:shadow-none { box-shadow: none !important; }
          .print\\:rounded-none { border-radius: 0 !important; }
          @page { size: A4; margin: 10mm; }
        }
      `}</style>
    </div>
  );
}

export default StudentMahiti;
